import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { Fade, Grid2 } from '@mui/material';
import Card from '../Card';
import DonutChart from '../DonutChart';
import Container from '../Container';
import BarChart from '../BarChart';
import { useReport } from 'core/contexts/reportsFilter';
import { CircularIndeterminate } from '@utriper/core-ui';
import { useCurrencies } from 'core/contexts/currency';

const HostReport = () => {
    const { t: translate } = useTranslation();

    const classes = useStyles();
    const { loadingHost, hostReports, dayLabels, monthsMap, getValuesByOrigin, getValues, getDateValues, getMonthlyValues, getTotalsHost } = useReport();
    const { currency, currencies } = useCurrencies();

    // TOTALS
    const reportTotals = useMemo(() => {
        const auxTotals = getTotalsHost(hostReports?.totals, hostReports?.rates);
        return auxTotals ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.totals, hostReports?.rates, currencies, currency]);

    // BY ORIGIN
    const ticketsSoldByOrigin = useMemo(() => {
        const auxTicketsSold = getValuesByOrigin(hostReports?.byOrigin, 'ticketsSold', hostReports?.rates);
        return auxTicketsSold ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byOrigin, hostReports?.rates, currencies, currency]);

    const totalPriceByOrigin = useMemo(() => {
        const auxTotalPrice = getValuesByOrigin(hostReports?.byOrigin, 'totalPrice', hostReports?.rates);
        return auxTotalPrice ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byOrigin, hostReports?.rates, currencies, currency]);

    const hostCommissionByOrigin = useMemo(() => {
        const auxHostCommission = getValuesByOrigin(hostReports?.byOrigin, 'hostCommission', hostReports?.rates);
        return auxHostCommission ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byOrigin, hostReports?.rates, currencies, currency]);

    const totalCommissionByOrigin = useMemo(() => {
        const auxTotalCommission = getValuesByOrigin(hostReports?.byOrigin, 'totalCommission', hostReports?.rates);
        return auxTotalCommission ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byOrigin, hostReports?.rates, currencies, currency]);

    const utriperCommissionByOrigin = useMemo(() => {
        const auxUtriperCommission = getValuesByOrigin(hostReports?.byOrigin, 'utriperCommission', hostReports?.rates);
        return auxUtriperCommission ?? [];
    }, [hostReports?.byOrigin, hostReports?.rates, currencies, currency]);

    // BY EXPERIENCER
    const ticketsSoldByExperiencer = useMemo(() => {
        const auxTicketsSold = getValues(hostReports?.byExperiencer, 'ticketsSold', hostReports?.rates);
        return auxTicketsSold ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byExperiencer, hostReports?.rates, currencies, currency]);

    const totalPriceByExperiencer = useMemo(() => {
        const auxTotalPrice = getValues(hostReports?.byExperiencer, 'totalPrice', hostReports?.rates);
        return auxTotalPrice ?? [];
    }, [hostReports?.byExperiencer, hostReports?.rates, currencies, currency]);

    const hostCommissionByExperiencer = useMemo(() => {
        const auxHostCommission = getValues(hostReports?.byExperiencer, 'hostCommission', hostReports?.rates);
        return auxHostCommission ?? [];
    }, [hostReports?.byExperiencer, hostReports?.rates, currencies, currency]);

    // BOOKING EVOLUTION
    const bookingEvolution = useMemo(() => {
        const auxSeries = getMonthlyValues(hostReports?.bookingMonthlyEvolution);

        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.bookingMonthlyEvolution]);

    // BY HOST AGENT
    const ticketsSoldByHostAgent = useMemo(() => {
        const auxTicketsSold = getValues(hostReports?.byHostAgent, 'ticketsSold', hostReports?.rates);
        return auxTicketsSold ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byHostAgent, hostReports?.rates, currencies, currency]);

    const totalPriceByHostAgent = useMemo(() => {
        const auxTotalPrice = getValues(hostReports?.byHostAgent, 'totalPrice', hostReports?.rates);
        return auxTotalPrice ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.byHostAgent, hostReports?.rates, currencies, currency]);

    const totalCommissionByHostAgent = useMemo(() => {
        const auxTotalCommission = getValues(hostReports?.byHostAgent, 'totalCommission', hostReports?.rates);
        return auxTotalCommission ?? [];
    }, [hostReports?.byHostAgent, hostReports?.rates, currencies, currency]);

    const hostCommissionByHostAgent = useMemo(() => {
        const auxHostCommission = getValues(hostReports?.byHostAgent, 'hostCommission', hostReports?.rates);
        return auxHostCommission ?? [];
    }, [hostReports?.byHostAgent, hostReports?.rates, currencies, currency]);

    const utriperCommissionByHostAgent = useMemo(() => {
        const auxUtriperCommission = getValues(hostReports?.byHostAgent, 'utriperCommission', hostReports?.rates);
        return auxUtriperCommission ?? [];
    }, [hostReports?.byHostAgent, hostReports?.rates, currencies, currency]);

    // EXPERIENCE DATES
    const experienceDates = useMemo(() => {
        const auxSeries = getDateValues(hostReports?.experienceDateDistribution);
        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.experienceDateDistribution]);

    // BOOKING DATES
    const bookingDates = useMemo(() => {
        const auxSeries = getDateValues(hostReports?.bookingDateDistribution);
        return auxSeries ?? [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostReports?.bookingDateDistribution]);

    return loadingHost ? (
        <div>
            <CircularIndeterminate />
        </div>
    ) : (
        <Fade in={!loadingHost}>
            <Grid2 container size={12} direction="column" width="100%" justifyContent="flex-start" alignItems="flex-start" gap="16px" pb={4}>
                {/* Cards Totals */}
                <Grid2 container size={{ xs: 6, md: 12 }} gap="8px" justifyContent="flex-start" alignItems="center" width="100%" flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
                    {reportTotals?.map(({ title, value, currency = null }) => (
                        <Card title={title} value={value} currency={currency} width={{ xs: '100%', sm: 'auto', md: '100%' }} />
                    ))}
                </Grid2>

                {/* Charts By Origin */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByOrigin?.length > 0 && (
                        <Grid2 size={{ xs: 2, md: 4 }} className={classes.chartContainer}>
                            <DonutChart title={translate('reports.host_report.tickets_sold_by_origin')} subtitle="Tickets" series={ticketsSoldByOrigin?.map((ticket) => ticket?.value)} optionLabels={ticketsSoldByOrigin?.map((ticket) => ticket?.name)} />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByOrigin?.length > 0 && (
                        <Grid2 size={{ xs: 2, md: 4 }} className={classes.chartContainer}>
                            <DonutChart title={translate('reports.host_report.total_price_by_origin')} subtitle={currency} series={totalPriceByOrigin?.map((ticket) => ticket?.value)} optionLabels={totalPriceByOrigin?.map((ticket) => ticket?.name)} />
                        </Grid2>
                    )}

                    {/* Host Commission */}
                    {hostCommissionByOrigin?.length > 0 && (
                        <Grid2 size={{ xs: 2, md: 4 }} className={classes.chartContainer}>
                            <DonutChart title={translate('reports.host_report.host_commission_by_origin')} subtitle={currency} series={hostCommissionByOrigin?.map((ticket) => ticket?.value)} optionLabels={hostCommissionByOrigin?.map((ticket) => ticket?.name)} />
                        </Grid2>
                    )}
                </Container>

                {/* Commission Charts */}
                <Container>
                    {hostCommissionByOrigin?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.host_commission_by_origin')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_commission'),
                                        data: hostCommissionByOrigin?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={hostCommissionByOrigin?.map((commission) => commission?.name)}
                                horizontal
                            />
                        </Grid2>
                    )}

                    {totalCommissionByOrigin?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.total_commission_by_origin')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_report.totals.total_commission'),
                                        data: totalCommissionByOrigin?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={totalCommissionByOrigin?.map((commission) => commission?.name)}
                                horizontal
                            />
                        </Grid2>
                    )}

                    {utriperCommissionByOrigin?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.utriper_commission_by_origin')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_report.totals.utriper_commission'),
                                        data: utriperCommissionByOrigin?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={utriperCommissionByOrigin?.map((commission) => commission?.name)}
                                horizontal
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Charts By Experiencer */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByExperiencer?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.tickets_sold_by_experiencer')}
                                subtitle={'Tickets'}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.tickets_sold'),
                                        data: ticketsSoldByExperiencer?.map((ticket) => ticket?.value),
                                    },
                                ]}
                                categories={ticketsSoldByExperiencer?.map((ticket) => ticket?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByExperiencer?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.total_price_by_experiencer')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.price_sold'),
                                        data: totalPriceByExperiencer?.map((totalPrice) => totalPrice?.value),
                                    },
                                ]}
                                categories={totalPriceByExperiencer?.map((totalPrice) => totalPrice?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Host Commission */}
                    {hostCommissionByExperiencer?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.host_commission_by_experiencer')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_commission'),
                                        data: hostCommissionByExperiencer?.map((hostCommission) => hostCommission?.value),
                                    },
                                ]}
                                categories={hostCommissionByExperiencer?.map((hostCommission) => hostCommission?.name)}
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Charts Evolution */}
                <Container>
                    {/* Booking Monthly Evolution */}
                    {bookingEvolution?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.booking_evolution')}
                                subtitle={currency}
                                series={[
                                    {
                                        name: translate('reports.sales'),
                                        data: bookingEvolution.map((bkg) => hostReports?.bookingMonthlyEvolution[bkg]),
                                    },
                                ]}
                                categories={bookingEvolution?.map((bkg) => monthsMap[bkg])}
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Charts By HostAgent */}
                <Container>
                    {/* Tickets Sold */}
                    {ticketsSoldByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.tickets_sold_by_host_agent')}
                                subtitle={'Tickets'}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.tickets_sold'),
                                        data: ticketsSoldByHostAgent?.map((ticket) => ticket?.value),
                                    },
                                ]}
                                categories={ticketsSoldByHostAgent?.map((ticket) => ticket?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Total Price */}
                    {totalPriceByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.total_price_by_host_agent')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.price_sold'),
                                        data: totalPriceByHostAgent?.map((totalPrice) => totalPrice?.value),
                                    },
                                ]}
                                categories={totalPriceByHostAgent?.map((totalPrice) => totalPrice?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Total Commission */}
                    {totalCommissionByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.total_commission_by_host_agent')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_report.totals.total_commission'),
                                        data: totalCommissionByHostAgent?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={totalCommissionByHostAgent?.map((commission) => commission?.name)}
                            />
                        </Grid2>
                    )}

                    {/* Host Commission */}
                    {hostCommissionByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.host_commission_by_host_agent')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_commission'),
                                        data: hostCommissionByHostAgent?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={hostCommissionByHostAgent?.map((commission) => commission?.name)}
                            />
                        </Grid2>
                    )}

                    {/* uTriper Commission */}
                    {utriperCommissionByHostAgent?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart
                                title={translate('reports.host_report.utriper_commission_by_host_agent')}
                                subtitle={currency}
                                label={'Tickets'}
                                series={[
                                    {
                                        name: translate('reports.host_report.totals.utriper_commission'),
                                        data: utriperCommissionByHostAgent?.map((commission) => commission?.value),
                                    },
                                ]}
                                categories={utriperCommissionByHostAgent?.map((commission) => commission?.name)}
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Date Charts */}
                <Container>
                    {/* Experience Date Distribution */}
                    {experienceDates?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart title={translate('reports.experience_dates')} subtitle={currency} label={'Tickets'} series={experienceDates} categories={dayLabels} horizontal />
                        </Grid2>
                    )}

                    {/* Booking Date Distribution */}
                    {bookingDates?.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <BarChart title={translate('reports.booking_dates')} subtitle={currency} label={'Tickets'} series={bookingDates} categories={dayLabels} horizontal />
                        </Grid2>
                    )}
                </Container>
            </Grid2>
        </Fade>
    );
};

export default HostReport;
